import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryStates, parseAsString } from 'nuqs';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { DEV_LOGIN_DEFAULT_EMAIL } from '@reflex/shared';
import {
  api,
  DEV_LOGIN_AUTO_SUPPRESS_KEY,
  postSignInPath,
  takeSessionEndedSignal,
} from '@/lib/api';
import { selectSignInProviders } from '@/lib/auth-providers';
import { useAuth } from '@/hooks/auth/useAuth';
import { useShowHiddenProviders } from '@/hooks/auth/useShowHiddenProviders';
import { SignInWithProviderButton } from '@/components/auth/SignInWithProviderButton';
import { SignInWithSso } from '@/components/auth/SignInWithSso';
import { getAuthProviderForm } from '@/lib/plugin-registry';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { AlertCircle, ArrowRight, Clock, LogOut, X } from 'lucide-react';
import { AuthLayout, authCardClassName } from './AuthLayout';
import { ConnectionErrorCard } from './ConnectionErrorCard';
import { LegalConsentLine } from '@/components/auth/LegalConsentLine';
import { cn } from '@reflex/ui/lib/utils';
import { describeDevLoginError, type DevLoginError } from './describeDevLoginError';

interface AuthErrorBanner {
  title: string;
  description: ReactNode;
}

/**
 * Map a server-supplied `?auth_error=...` code to a friendly title and
 * description shown above the provider buttons.
 */
function describeAuthError(code: string): AuthErrorBanner {
  switch (code) {
    case 'domain_not_allowed':
      return {
        title: "You don't have access yet",
        description:
          "We're in early access, so sign-in is invite-only. Your administrator restricted sign-in to specific email domains, and yours isn't on the list yet. Reach out to them if you think your account should have access.",
      };
    case 'access_denied':
      return {
        title: 'Sign-in cancelled',
        description: 'The OAuth provider denied the request. Try again to continue.',
      };
    case 'no_code':
      return {
        title: 'Sign-in incomplete',
        description: 'The OAuth provider returned without an authorization code. Try again.',
      };
    case 'exchange_failed':
      return {
        title: 'Sign-in failed',
        description:
          'We could not exchange the OAuth code for a session. Try again, or contact an administrator if the problem persists.',
      };
    case 'sso_unavailable':
      return {
        title: 'Single sign-on is not available',
        description:
          'We could not find company SSO from that email or organization. Check your work email, open your company sign-in link, or ask your administrator whether SSO is set up.',
      };
    case 'sso_required':
      return {
        title: 'Your organization requires SSO',
        description:
          'Password and social sign-in are turned off for your organization. Use "Sign in with company SSO" below to continue with your identity provider.',
      };
    case 'sso_policy_conflict':
      return {
        title: 'Your account has conflicting SSO requirements',
        description:
          'Your account belongs to multiple companies that require different SSO providers. Ask an administrator to resolve this before you sign in.',
      };
    case 'email_unverified_conflict':
      return {
        title: 'This email is not verified',
        description:
          'Your sign-in provider did not verify this address, and an account already uses it. Sign in with the provider you used before, or verify the address with that provider and try again.',
      };
    case 'sso_login_failed':
      return {
        title: 'SSO sign-in did not finish',
        description:
          'Your identity provider signed you in, but we could not connect you to that organization. Ask your administrator to invite you or enable automatic provisioning.',
      };
    default:
      return {
        title: 'Sign-in failed',
        description: `The OAuth provider returned an error: ${code}.`,
      };
  }
}

/**
 * The identity to offer under local Vite dev (`import.meta.env.DEV`) when the
 * server does not advertise its own dev-login list — shared canonical constant
 * with the server's `/auth/dev-login` fallback.
 */
const DEV_LOGIN_FALLBACK_EMAIL = DEV_LOGIN_DEFAULT_EMAIL;

/**
 * The single sign-in screen, served by both `/login` and `/signup` (the
 * latter kept for old links). There is no separate create-account page:
 * every provider flow either signs the visitor into their existing account
 * or creates one on the spot. OAuth callbacks are create-or-sign-in on the
 * server; email/password offers an inline switch to a register form.
 *
 * When the visitor is already signed in, a "you're already signed in" card
 * renders instead so a stale bookmark doesn't try to sign them in again.
 *
 * Every state is wrapped in {@link AuthLayout}, which supplies the Reflex
 * brand lockup and backdrop so the auth flow feels on-brand.
 */
export function AuthScreen() {
  const { providers, authenticated, user, logout, devLogin, connectionError, refreshSession } =
    useAuth();
  // The server advertises dev sign-in identities on `/auth/me` whenever
  // `/auth/dev-login` is mounted (dev and seeded preview servers — never
  // production). That signal is the source of truth: the built SPA has
  // Vite's compile-time DEV flag off, so gating on `import.meta.env.DEV`
  // alone would hide the affordance exactly where it matters most (tunneled
  // seeded previews). The DEV fallback keeps the button visible for local
  // Vite dev against a server that hasn't enabled dev-login, where clicking
  // it surfaces the actionable error below.
  const devLoginUsers = devLogin?.users ?? (import.meta.env.DEV ? [DEV_LOGIN_FALLBACK_EMAIL] : []);
  const [devLoginError, setDevLoginError] = useState<DevLoginError | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [{ auth_error: authError }, setAuthErrorParams] = useQueryStates({
    auth_error: parseAsString,
  });
  const errorBanner = authError ? describeAuthError(authError) : null;
  // Read once on mount: the flag is one-shot, and a re-render must not
  // re-read (and re-clear) storage. A server-reported auth error is the more
  // specific explanation, so it takes the slot when both are present.
  const [sessionEnded, setSessionEnded] = useState(() => takeSessionEndedSignal());
  const showSessionEnded = sessionEnded && !errorBanner;

  // Split providers: 'password' providers (the email-auth plugin) contribute a
  // self-contained form through the registry; OAuth providers render redirect
  // buttons. Operators can hide either kind from the login screen, and hidden
  // providers stay revealed for visitors who opt in once via
  // `?showHidden=true`.
  const showHidden = useShowHiddenProviders();
  const { oauth: advertisedOauthEntries, password: visiblePasswordEntries } = selectSignInProviders(
    providers,
    showHidden,
  );
  // OIDC contributes the company SSO capability, but its generic provider
  // button cannot choose a company. Render the dedicated SSO entry once and
  // keep ordinary social providers in this list.
  const visibleOauthEntries = advertisedOauthEntries.filter(([name]) => name !== 'oidc');
  const hasEnterpriseSso = 'oidc' in providers;
  const hasPasswordProvider = visiblePasswordEntries.length > 0;
  // Drives the "nothing to sign in with" copy and the terms footer. Keyed off
  // the *visible* providers: an instance whose every provider is hidden has
  // nothing to offer, and saying so beats a card with a title and no buttons.
  const hasVisibleProvider =
    visibleOauthEntries.length > 0 || hasPasswordProvider || hasEnterpriseSso;

  // A contributed password form (email/password) can reveal itself into a full
  // form that takes over the card. While expanded, the host hides the OAuth
  // buttons and the dev affordance so the form is the sole focus. The form
  // reports its reveal/collapse state through `onExpandedChange`.
  const [emailExpanded, setEmailExpanded] = useState(false);

  async function handleDevLogin(email: string) {
    setDevLoginError(null);
    try {
      await api.devLogin(email);
      // Land in the app, mirroring the email form's onSuccess. A bare
      // reload() would re-render /login as the "already signed in" card and
      // cost a second click.
      window.location.assign(postSignInPath());
    } catch (err) {
      setDevLoginError(describeDevLoginError(err));
    }
  }

  // Seeded previews skip the login screen entirely: when the server
  // advertises an auto sign-in identity, sign in as it on load. Attempted at
  // most once per mount, and suppressed for the rest of the browser session
  // after an explicit sign-out (see DEV_LOGIN_AUTO_SUPPRESS_KEY) so the user
  // can still pick a different identity from the buttons below. Calls the API
  // directly (not handleDevLogin) so state updates only happen in the promise
  // callbacks, never synchronously in the effect body.
  const autoLoginEmail = devLogin?.autoLoginEmail;
  const autoLoginAttempted = useRef(false);
  useEffect(() => {
    if (authenticated || !autoLoginEmail) return;
    if (autoLoginAttempted.current) return;
    if (sessionStorage.getItem(DEV_LOGIN_AUTO_SUPPRESS_KEY)) return;
    autoLoginAttempted.current = true;
    api.devLogin(autoLoginEmail).then(
      () => window.location.assign(postSignInPath()),
      (err: unknown) => setDevLoginError(describeDevLoginError(err)),
    );
  }, [authenticated, autoLoginEmail]);

  if (authenticated && user) {
    async function handleLogout() {
      setIsLoggingOut(true);
      try {
        await logout();
      } finally {
        setIsLoggingOut(false);
      }
    }
    const displayName = user.name?.trim() || user.login || user.email;
    const hasSeparateEmail = Boolean(user.email) && displayName !== user.email;
    const title = "You're already signed in";
    const description = 'Pick up right where you left off, or log out to use a different account.';
    return (
      <AuthLayout>
        <Card className={cn(authCardClassName, 'relative overflow-hidden')}>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -top-px h-32 bg-gradient-to-b from-primary/[0.07] to-transparent"
          />
          <CardHeader className="relative items-center text-center">
            <div
              className="flex flex-col items-center gap-3"
              data-testid="already-signed-in-account"
            >
              <div className="relative rounded-full bg-gradient-to-br from-primary/25 to-primary/5 p-1 shadow-sm">
                <UserAvatar
                  userName={displayName}
                  avatarUrl={user.avatarUrl ?? undefined}
                  size="lg"
                  className="ring-2 ring-card"
                />
                <span
                  className="absolute bottom-0.5 right-0.5 size-4 rounded-full bg-status-success ring-2 ring-card"
                  aria-hidden
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-base font-semibold leading-tight text-foreground">
                  {displayName}
                </span>
                {hasSeparateEmail && (
                  <span className="text-sm text-muted-foreground">{user.email}</span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-1 h-7 rounded-full px-3 text-xs text-muted-foreground"
                onClick={() => void handleLogout()}
                disabled={isLoggingOut}
                data-testid="login-page-logout"
              >
                <LogOut className="icon-xs" />
                {isLoggingOut ? 'Logging out…' : 'Log out'}
              </Button>
            </div>
            <PanelTitle className="mt-5 text-2xl tracking-tight">{title}</PanelTitle>
            <CardDescription className="mx-auto max-w-[17rem] text-balance leading-relaxed">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="relative flex flex-col gap-3">
            <Button size="lg" asChild data-testid="already-signed-in-continue">
              <Link to="/">
                Continue as {displayName}
                <ArrowRight className="icon-md" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  // `/auth/me` never answered, so `providers` is empty because we could not
  // ask, not because the instance has none configured. Say that instead of
  // rendering the sign-in card with a bare "no providers" line.
  if (connectionError) {
    return (
      <AuthLayout>
        <ConnectionErrorCard onRetry={refreshSession} />
      </AuthLayout>
    );
  }

  const title = 'Welcome to Reflex';

  return (
    <AuthLayout>
      <Card className={authCardClassName} data-testid="auth-screen">
        <CardHeader className="items-center pb-1 text-center">
          <PanelTitle className="text-2xl tracking-tight">{title}</PanelTitle>
          <CardDescription>Choose a method to sign in or create an account.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {errorBanner && (
            <div
              role="alert"
              className="relative flex gap-3 rounded-lg border border-destructive/25 bg-destructive/[0.07] py-3 pl-3.5 pr-9 text-left"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-destructive">{errorBanner.title}</p>
                <p className="text-sm leading-relaxed text-destructive/80">
                  {errorBanner.description}
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() =>
                  void setAuthErrorParams({ auth_error: null }, { history: 'replace' })
                }
                className="absolute right-1.5 top-1.5 rounded-md p-1.5 text-destructive/60 transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40"
              >
                <X className="size-3.5" />
              </button>
            </div>
          )}
          {showSessionEnded && (
            <div
              role="status"
              data-testid="session-ended-notice"
              className="relative flex gap-3 rounded-lg border border-border bg-muted/40 py-3 pl-3.5 pr-9 text-left"
            >
              <Clock className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">Your session ended</p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Sign in again to pick up where you left off.
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setSessionEnded(false)}
                className="absolute right-1.5 top-1.5 rounded-md p-1.5 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-3.5" />
              </button>
            </div>
          )}
          {!hasVisibleProvider && (
            <PageDescription className="text-center">
              No authentication providers are configured.
            </PageDescription>
          )}
          {visiblePasswordEntries.map(([name]) => {
            const Form = getAuthProviderForm(name);
            return Form ? (
              <Form
                key={name}
                hasOtherProviders={visibleOauthEntries.length > 0 || hasEnterpriseSso}
                onSuccess={() => window.location.assign(postSignInPath())}
                onExpandedChange={setEmailExpanded}
              />
            ) : null;
          })}
          {!emailExpanded && (
            <>
              {visibleOauthEntries.map(([name, { label }], index) => (
                <SignInWithProviderButton
                  key={name}
                  name={name}
                  label={label}
                  variant={!hasPasswordProvider && index === 0 ? 'primary' : 'outline'}
                />
              ))}
              {/* Company SSO, as a secondary text link under the providers. A
                  known organization starts immediately; a first-time visitor
                  gets the work-email discovery form. The generic OIDC provider
                  is filtered above so this renders once. */}
              {hasEnterpriseSso && <SignInWithSso />}
              {devLoginUsers.map((email) => (
                <Button
                  key={email}
                  variant="outline"
                  className="w-full border-dashed text-muted-foreground"
                  size="lg"
                  onClick={() => void handleDevLogin(email)}
                  data-testid={`dev-login-${email}`}
                >
                  Dev: Sign in as {email}
                </Button>
              ))}
              {devLoginError && (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Dev login failed</AlertTitle>
                  <AlertDescription>
                    <p>{devLoginError.message}</p>
                    {devLoginError.hint && (
                      <pre className="mt-2 whitespace-pre-wrap rounded bg-destructive/10 px-2 py-1.5 font-mono text-xs">
                        {devLoginError.hint}
                      </pre>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
          {hasVisibleProvider && <LegalConsentLine />}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
